import { AxiosError } from "axios";
import { useCallback } from "react";
import { initializeEmployerDeliveryClient } from "services/CMS/KontentAI/unumClient";


type BaseOption = {
  type: string;
  depth?: number;
  limit?: number;
  equals?: { key: string; value: string };
  resolver: (arg: any) => any;
};

function useBaseKontentItems<Options extends BaseOption>(options: Options) {
  const { type, depth = 0, limit, equals, resolver } = options;

  const fetchData = useCallback(async (): Promise<{
    isError: boolean;
    errorMessage?: string;
    data?: Awaited<ReturnType<Options["resolver"]>>;
  }> => {
    let data: Awaited<ReturnType<Options["resolver"]>>;

    try {
      const client = await initializeEmployerDeliveryClient();
      let query = client.items().type(type).depthParameter(depth);

      if (equals) query = query.equalsFilter(equals.key, equals.value);
      if (limit) query = query.limitParameter(limit);

      const res = await query.toPromise();

      const items = limit === 1 ? res.data.items[0] : res.data.items;

      data = await resolver(items);

      return { isError: false, errorMessage: undefined, data };
    } catch (err) {
      let errorMessage = "An unknown error occurred";

      if (err instanceof AxiosError && err.response) {
        errorMessage = `API Error: ${err.response.status} ${err.response.statusText}`;
      } else if (err instanceof Error) {
        errorMessage = `Error: ${err.message}`;
      }
      console.error({ isError: true, errorMessage, data: undefined });
      return { isError: true, errorMessage, data: undefined };
    }
  }, [type, depth, limit, equals, resolver]);

  return { fetchData };
}

export const useKontentItems = <Options extends BaseOption>(options: Options) =>
  useBaseKontentItems(options);
